import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Input, Stack } from '@chakra-ui/react'
import type { UpdateVenueRequest } from '@clsplus/api-types/api-admin'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { useLoaderData, useNavigate, useParams, useRevalidator } from 'react-router-dom'
import * as z from 'zod'

import { venueDetailsQuery } from '../../../api/get-venue-details'
import { updateVenueDetails } from '../../../api/update-venue-details'

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) =>
    await queryClient.ensureQueryData(venueDetailsQuery(params.venueId || ''))

const venueEditSchema = z.object({
  name: z.string().min(1, { message: 'Please enter a venue name' }),
})

type VenueEditFormData = z.infer<typeof venueEditSchema>

export default function VenueEdit() {
  const params = useParams()
  const queryClient = useQueryClient()
  const initialData = useLoaderData() as Awaited<ReturnType<ReturnType<typeof loader>>>
  const navigate = useNavigate()
  const revalidator = useRevalidator()

  const venueId = params.venueId || ''

  const { data: venueDetails } = useQuery({
    ...venueDetailsQuery(venueId),
    initialData,
  })

  const updateVenueMutation = useMutation({
    mutationFn: (formData: UpdateVenueRequest) => updateVenueDetails(venueId, formData),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['venues'] }),
        queryClient.invalidateQueries({ queryKey: ['venue', venueId] }),
      ])
      revalidator.revalidate()
      navigate(`/venues/${venueId}`)
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<VenueEditFormData>({
    defaultValues: {
      name: venueDetails.name,
    },
    resolver: zodResolver(venueEditSchema),
  })

  const handleSubmitClick = (formData: VenueEditFormData) => {
    updateVenueMutation.mutate(formData)
  }

  return (
    <Flex direction="column" w="100%">
      <Heading as="h2" size="lg">
        {venueDetails.name}
      </Heading>
      <Stack
        as="form"
        noValidate
        id="venue-edit"
        spacing={4}
        maxW="container.sm"
        mt={8}
        onSubmit={handleSubmit(handleSubmitClick)}
      >
        <FormControl isRequired isInvalid={!!errors.name}>
          <FormLabel>Name</FormLabel>
          <Input {...register('name')} />
          {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={4} mt={8}>
        <Button
          type="submit"
          form="venue-edit"
          variant="primary"
          isDisabled={!isDirty}
          isLoading={updateVenueMutation.isLoading}
        >
          Save changes
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(`/venues/${venueId}`)}
          isDisabled={updateVenueMutation.isLoading}
        >
          Cancel
        </Button>
      </Stack>
    </Flex>
  )
}
